/**
 * The plans, as a plain list — ship one's whole reporting surface.
 *
 * Not the blotter. That is `Section.kind: "blotter"` and a later phase: the
 * screener grid over these same rows, with a row cursor driving the chart. This
 * is deliberately less than that, because the loop the plan doc cares about is
 * write a plan → the simulator fills it → see what happened, and a list closes
 * it without a section, a config or a contract change.
 *
 * Every number here is derived (`lib/plan-math.ts`). Nothing in this file reads
 * a stored total, because there are none to read.
 */

import type { Execution, Plan, Trade } from "@qufolio/model";

import { plannedR, realisedR, riskPerShare } from "../lib/plan-math";

export interface PlanListProps {
  plans: readonly Plan[];
  trades: readonly Trade[];
  executions: readonly Execution[];
  onPick?: (plan: Plan) => void;
}

/** How a status reads, and whether it is still live. */
const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  armed: "Armed",
  filled: "Open",
  missed: "Missed",
  cancelled: "Cancelled",
  closed: "Closed",
};

export function PlanList({ plans, trades, executions, onPick }: PlanListProps) {
  if (plans.length === 0) {
    // An empty state that says what to do, rather than an empty table. The
    // first plan is the one thing a new journal needs.
    return (
      <div style={{ padding: 12, color: "var(--qf-text-muted)" }}>
        No plans yet. Press <strong>New</strong> above, or drag from an entry to a
        stop on the chart.
      </div>
    );
  }

  const byPlan = new Map<string, Execution[]>();
  for (const trade of trades) {
    if (!trade.planId) continue;
    const fills = executions.filter((e) => e.tradeId === trade.id);
    byPlan.set(trade.planId, [...(byPlan.get(trade.planId) ?? []), ...fills]);
  }

  return (
    <table style={TABLE}>
      <thead>
        <tr>
          {["Symbol", "", "Entry", "Stop", "1R", "Planned", "Result", "Status"].map((head) => (
            <th key={head} style={TH}>
              {head}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {plans.map((plan) => {
          const fills = byPlan.get(plan.id) ?? [];
          const result = fills.length > 0 ? realisedR(plan, fills) : null;
          return (
            <tr
              key={plan.id}
              onClick={() => onPick?.(plan)}
              style={{ cursor: onPick ? "pointer" : "default" }}
            >
              <td style={TD}>{plan.symbol}</td>
              <td style={{ ...TD, color: "var(--qf-text-muted)" }}>
                {plan.direction === "long" ? "L" : "S"}
              </td>
              <td style={NUM}>{plan.entry.toFixed(2)}</td>
              {/* The live stop, which is what managing the trade has moved it
                  to — never the frozen one, which is what R is measured from
                  and is deliberately a different number. */}
              <td style={NUM}>{plan.stop.toFixed(2)}</td>
              <td style={NUM}>{riskPerShare(plan).toFixed(2)}</td>
              <td style={NUM}>{signed(plannedR(plan))}</td>
              <td style={{ ...NUM, color: colourOf(result) }}>
                {result === null ? "—" : signed(result)}
              </td>
              <td style={TD}>{STATUS_LABEL[plan.status] ?? plan.status}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const signed = (r: number): string => `${r >= 0 ? "+" : ""}${r.toFixed(2)}R`;

/** Muted until there is a result: an unfilled plan is not a flat outcome. */
function colourOf(result: number | null): string {
  if (result === null) return "var(--qf-text-muted)";
  return result >= 0 ? "var(--qf-positive)" : "var(--qf-negative)";
}

const TABLE: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontVariantNumeric: "tabular-nums",
};
const TH: React.CSSProperties = {
  textAlign: "left",
  padding: "4px 8px",
  color: "var(--qf-text-muted)",
  fontWeight: 500,
  borderBottom: "1px solid var(--qf-border)",
};
const TD: React.CSSProperties = { padding: "3px 8px" };
const NUM: React.CSSProperties = { ...TD, textAlign: "right" };
